import { Divider, Tooltip } from "antd-v5";
import { isEmpty } from "lodash";
import moment from "moment";
import React, { useMemo } from "react";
import { FindingSummary } from "types/api/findingSummary";
import { CellRenderers } from "utils/tableUtils";
import {
  getMatchedValueLabel,
  getRuleColor,
  getRuleDisplayLabel,
} from "views/Findings/FindingDetails/PinnableFields/findingAssetCorrelationUtils";

import ACTagColored from "ac-components/ac-tag-colored/ACTagColored";

interface AssetCorrelationFieldsParams {
  finding: FindingSummary | undefined;
  assetName?: string;
}

export const useFindingAssetCorrelationFields = ({
  finding,
  assetName,
}: AssetCorrelationFieldsParams) => {
  return useMemo(() => {
    const context = finding?.assetMatchingContext;
    if (!context) return [];

    return [
      {
        label: "subSection",
        name: "correlationSubSection",
        path: "subSection",
        span: 24,
        group: "asset",
        disabledPin: true,
        formatter: () => (
          <>
            <Divider type="horizontal" />
            <div style={{ marginLeft: 4 }}>
              <strong>Finding - Asset Correlation Details</strong>
            </div>
          </>
        ),
      },
      {
        label: "Asset",
        name: "correlationAsset",
        path: "armorcodeAssetId",
        group: "asset",
        span: 24,
        disabledPin: true,
        valueClassName: "text-ellipsis",
        formatter: () => {
          return assetName ? (
            <Tooltip title={assetName} placement="topLeft">
              <span>{assetName}</span>
            </Tooltip>
          ) : (
            CellRenderers.VALUE_OR_NA()
          );
        },
      },
      {
        label: "Rule",
        name: "correlationRule",
        path: "assetMatchingContext.rule",
        group: "asset",
        span: 24,
        disabledPin: true,
        formatter: (rule: string) => {
          if (!rule) return CellRenderers.VALUE_OR_NA();
          const ruleLabel = getRuleDisplayLabel(rule);
          return (
            <ACTagColored
              title={ruleLabel}
              label={ruleLabel}
              color={getRuleColor(rule)}
              style={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
              }}
            />
          );
        },
      },
      {
        label: "Match Date",
        name: "correlationMatchDate",
        path: "assetMatchingContext.matchDate",
        group: "asset",
        span: 24,
        disabledPin: true,
        formatter: (matchDate: number) => {
          if (!matchDate) return CellRenderers.VALUE_OR_NA();
          return moment(matchDate).format("MMM DD, YYYY hh:mm A");
        },
      },
      {
        label: "Matched Values",
        name: "correlationMatchedValues",
        path: "assetMatchingContext.correlatedValues",
        group: "asset",
        span: 24,
        disabledPin: true,
        containerClassName: "flex-column",
        formatter: (values: Record<string, string | null> | null) => {
          if (!values || isEmpty(values)) return CellRenderers.VALUE_OR_NA();
          return (
            <ul
              className="m-t-xs m-b-sm"
              style={{
                paddingLeft: 24,
                listStyleType: "disc",
                listStylePosition: "inside",
              }}
            >
              {Object.entries(values).map(([key, val]) => (
                <li
                  key={key}
                  className="m-b-xs text-ellipsis"
                  title={`${getMatchedValueLabel(key)}: ${val || "N/A"}`}
                >
                  {getMatchedValueLabel(key)}: <strong>{val || "N/A"}</strong>
                </li>
              ))}
            </ul>
          );
        },
      },
    ];
  }, [finding?.assetMatchingContext, assetName]);
};
